import { Injectable, inject } from '@angular/core';
import { Observable, throwError, forkJoin } from 'rxjs';
import { map, switchMap, catchError, finalize, tap } from 'rxjs/operators';
import { CaseFormRepository } from '../../../infrastructure/repositories/case-form.repository';
import { CaseFormState } from '../../states/case-form.state';
import { GetCaseFormLastVersionQuery } from './get-case-form-last-version.query';
import { CaseFormDetailEntity } from '../../../domain/entities/case-form-detail.entity';
import { CaseFormFieldEntity, CaseFormFieldType } from '../../../domain/entities/case-form-field.entity';
import { FormVersionDetailRepositoryResponseDto } from '../../../infrastructure/dtos/form-version-detail-repository-response.dto';
import { FormVersionSummaryResponseDto } from '../../../infrastructure/dtos/form-version-summary-response.dto';
import { FormVersionFieldResponseDto } from '../../../infrastructure/dtos/form-version-field-response.dto';

/**
 * Get Case Form Last Version Use Case
 * Retrieves the last version of a case form (most recent by createdOn) with all its fields
 *
 * Makes 3 API calls:
 * 1. GET /cases/forms?Filter=... - Get base case form info by ID
 * 2. GET /cases/forms/{formId}/versions - Get all versions
 * 3. GET /cases/forms/versions/{versionId} - Get latest version details with fields
 */
@Injectable({ providedIn: 'root' })
export class GetCaseFormLastVersionUseCase {
  private readonly _repository = inject(CaseFormRepository);
  private readonly _state = inject(CaseFormState);

  public execute(query: GetCaseFormLastVersionQuery): Observable<CaseFormDetailEntity> {
    this._state.setLoadingCaseForms(true);
    this._state.clearErrorCaseForms();

    // Step 1: Get base case form info and all versions in parallel
    return forkJoin({
      caseForm: this._repository.getCaseFormById(query.formId),
      versions: this._repository.getCaseFormVersionsByFormId(query.formId)
    }).pipe(
      switchMap(({ caseForm, versions }) => {
        if (!caseForm) {
          throw new Error(`Case form ${query.formId} not found`);
        }

        // Sort versions by createdOn descending and get the latest one
        const sortedVersions = [...versions.data].sort(
          (a: FormVersionSummaryResponseDto, b: FormVersionSummaryResponseDto) =>
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
        );

        const latestVersion = sortedVersions[0];

        if (!latestVersion) {
          throw new Error(`No versions found for case form ${query.formId}`);
        }

        // Step 2: Get version details with fields
        return this._repository
          .getCaseFormVersionDetailByVersionId(latestVersion.versionId)
          .pipe(
            map((versionDetail: FormVersionDetailRepositoryResponseDto) => {
              // Build CaseFormDetailEntity
              const detailEntity = new CaseFormDetailEntity();

              // Data from base case form
              detailEntity.id = caseForm.id;
              detailEntity.name = caseForm.name;
              detailEntity.displayName = caseForm.name;
              detailEntity.description = caseForm.description;
              detailEntity.caseTypeId = caseForm.caseTypeId;
              detailEntity.caseTypeName = caseForm.caseTypeName ?? '';
              detailEntity.createdAt = caseForm.createdAt;
              detailEntity.updatedAt = caseForm.updatedAt;
              detailEntity.createdBy = 'system';
              detailEntity.updatedBy = 'system';

              // Data from version detail
              detailEntity.status = versionDetail.status;
              detailEntity.statusName = versionDetail.statusName;
              detailEntity.versionId = versionDetail.versionId;
              detailEntity.versionNumber = versionDetail.versionNumber;

              // Map fields
              detailEntity.fields = versionDetail.fields.map((field: FormVersionFieldResponseDto) => {
                const fieldEntity = new CaseFormFieldEntity();
                fieldEntity.id = field.fieldId;
                fieldEntity.name = field.label.toLowerCase().replace(/\s+/g, '_');
                fieldEntity.label = field.label;
                fieldEntity.helperText = field.helperText ?? undefined;
                fieldEntity.placeholder = field.placeholder ?? undefined;
                fieldEntity.fieldType = field.fieldType as CaseFormFieldType;
                fieldEntity.isRequired = field.isRequired;
                fieldEntity.isVisibleToRequester = field.isVisibleToRequester;
                fieldEntity.isHiddenByDefault = field.isHiddenByDefault;
                fieldEntity.isSystem = field.isSystem;
                fieldEntity.sensitivityCategory = field.sensitivityCategory;
                fieldEntity.visibilityRule = field.visibilityRule ? {
                  sourceFieldId: field.visibilityRule.sourceFieldId,
                  operator: field.visibilityRule.operator,
                  value: field.visibilityRule.value || '',
                  show: field.visibilityRule.show,
                } : null;
                fieldEntity.sortOrder = field.sortOrder;
                fieldEntity.options = undefined;
                fieldEntity.defaultValue = undefined;
                fieldEntity.validationRules = undefined;
                fieldEntity.constraints = field.constraints || [];
                return fieldEntity;
              });

              return detailEntity;
            })
          );
      }),
      tap((detailEntity) => this._state.setCaseFormSelectedToEdit(detailEntity)),
      catchError((error) => {
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          `Failed to load case form version for form ${query.formId}`;
        this._state.setErrorCaseForms(errorMessage);
        return throwError(() => error);
      }),
      finalize(() => this._state.setLoadingCaseForms(false))
    );
  }
}
